// eslint-disable-next-line no-use-before-define
import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DropDown from "../dropDown/dropDown";
import SearchField from "../searchField/searchField";
import Progress from "../progress/progress";
import FetchError from "../fetchError/fetchError";
import { getYears } from "../../../utils/compensation/compensation";
// eslint-disable-next-line import/no-cycle
import {
  filterCompensations,
  clearFilteredCompensations,
} from "../../../redux";

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  searchContainer: {
    marginRight: "3rem",
    marginLeft: "3rem",
  },
  searchResultContainer: {},
  searchResultText: {
    ...theme.typography.button,
    textTransform: "none",
    marginRight: theme.spacing(2),
  },
  dropdown: {
    backgroundColor: theme.palette.background.default,
    height: "40px",
    width: "165px",
  },
}));

/**
 * Shared search controls component for compensation periods.
 * Eliminates duplication across pharmacy, pharmaCompany, and oriolaAdmin components.
 */
function CompensationSearchControls({
  currentYear,
  setCurrentYear,
  periods,
  filteredCompensationsMap,
  filteredCompensationCount,
  searching,
  searchError,
  organizationIds,
  profitCenters,
  searchFieldWidth,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const filteredCount = filteredCompensationCount;
  const searchResult = `${t("hits")} ${filteredCount}`;

  const handleSearch = text => {
    if (text == null || !periods || periods.length === 0) {
      dispatch(clearFilteredCompensations());
      return;
    }
    const start = periods[periods.length - 1].periodStartDate;
    const end = periods[0].periodEndDate;
    dispatch(filterCompensations(start, end, text, organizationIds, profitCenters));
  };

  const handleClear = () => {
    dispatch(clearFilteredCompensations());
  };

  return (
    <div className={classes.searchBar}>
      <DropDown
        className={classes.dropdown}
        value={currentYear}
        noSelectionLabel="select"
        options={getYears()}
        onSelect={year => setCurrentYear(year)}
      />
      <div className={classes.searchContainer}>
        <SearchField
          style={{ width: searchFieldWidth }}
          placeholder={t("searchPlaceholder")}
          onSearch={handleSearch}
          onClear={handleClear}
        />
      </div>
      {filteredCompensationsMap != null && (
        <div className={classes.searchResultContainer}>
          <Typography className={classes.searchResultText} color="textPrimary">
            {searchResult}
          </Typography>
        </div>
      )}
      <Progress show={searching} />
      <FetchError error={searchError} />
    </div>
  );
}

CompensationSearchControls.propTypes = {
  currentYear: PropTypes.string.isRequired,
  setCurrentYear: PropTypes.func.isRequired,
  periods: PropTypes.arrayOf(
    PropTypes.shape({
      periodStartDate: PropTypes.string,
      periodEndDate: PropTypes.string,
      key: PropTypes.string,
    })
  ).isRequired,
  filteredCompensationsMap: PropTypes.instanceOf(Map),
  filteredCompensationCount: PropTypes.number,
  searching: PropTypes.bool,
  searchError: PropTypes.node,
  organizationIds: PropTypes.arrayOf(PropTypes.string),
  profitCenters: PropTypes.arrayOf(PropTypes.string),
  searchFieldWidth: PropTypes.string,
};

CompensationSearchControls.defaultProps = {
  filteredCompensationsMap: null,
  filteredCompensationCount: 0,
  searching: false,
  searchError: null,
  organizationIds: null,
  profitCenters: null,
  searchFieldWidth: "410px",
};

export default CompensationSearchControls;
